function pill(label: string, tone: "ready" | "accent" = "accent") {
  const background =
    tone === "ready" ? "rgba(44, 93, 67, 0.14)" : "rgba(41, 84, 103, 0.1)";
  const color = tone === "ready" ? "#214735" : "#295467";

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.42rem",
        borderRadius: 999,
        padding: "0.3rem 0.64rem",
        background,
        color,
        fontSize: "0.74rem",
        fontWeight: 700,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          borderRadius: 999,
          background: color,
        }}
      />
      {label}
    </span>
  );
}

function formatLastSaved(savedAt: string | null) {
  if (!savedAt) {
    return "Not yet saved in this browser";
  }

  const timestamp = new Date(savedAt);
  if (Number.isNaN(timestamp.getTime())) {
    return "Saved locally";
  }

  return `Last saved ${new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(timestamp)}`;
}

export default function StudioWorkspaceStatusBar(props: {
  draftRestored: boolean;
  lastSavedAt: string | null;
  onSave: () => void;
  onReset: () => void;
}) {
  return (
    <section
      aria-label="Studio workspace status"
      style={{
        marginTop: "1.15rem",
        display: "flex",
        justifyContent: "space-between",
        gap: "0.9rem",
        flexWrap: "wrap",
        alignItems: "center",
        borderRadius: 24,
        border: "1px solid rgba(54, 44, 35, 0.08)",
        background:
          "linear-gradient(180deg, rgba(255, 252, 248, 0.96), rgba(251, 248, 243, 0.98))",
        padding: "0.95rem 1rem",
      }}
    >
      <div style={{ display: "grid", gap: "0.55rem" }}>
        <div style={{ display: "flex", gap: "0.55rem", flexWrap: "wrap" }}>
          {props.draftRestored ? pill("Draft restored", "ready") : pill("Local draft")}
          {pill(formatLastSaved(props.lastSavedAt))}
        </div>
        <div style={{ color: "#5b534b", lineHeight: 1.55 }}>
          Workspace draft stays local to this browser and preserves the current
          preview-only Studio posture. Reset clears browser-local Studio changes only,
          so it is safe to use when install-day review needs a clean restart.
        </div>
      </div>

      <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={props.onSave}
          style={{
            borderRadius: 999,
            padding: "0.78rem 1rem",
            border: "1px solid rgba(41, 84, 103, 0.18)",
            background: "linear-gradient(135deg, #173847, #285466)",
            color: "#f7f4ee",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Save draft
        </button>
        <button
          type="button"
          onClick={props.onReset}
          aria-label="Reset Studio draft"
          style={{
            borderRadius: 999,
            padding: "0.78rem 1rem",
            border: "1px solid rgba(162, 62, 62, 0.18)",
            background: "rgba(162, 62, 62, 0.08)",
            color: "#812d2d",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Reset local draft
        </button>
      </div>
    </section>
  );
}
